/**
 * Cooling start → finish on the composer. Times are HH:mm on the viewed day;
 * finish blank means "now" (today only). Express works out the minutes and
 * the time verdict — these mirror it for the live preview.
 */

import moment from 'moment'
import { FOOD_SAFETY_COOLING_SUGGESTED_TARGET } from 'src/constants/foodSafety'

/** One-tap "went in to cool" offsets (minutes before finish). */
export const FOOD_SAFETY_COOLING_START_CHIPS = Object.freeze([
  { minutes: 30, label: '30 min ago' },
  { minutes: 60, label: '1 hr ago' },
  { minutes: 90, label: '1½ hr ago' },
  { minutes: 120, label: '2 hr ago' },
])

const LONGEST_WINDOW_MINUTES = 48 * 60
const FUTURE_TOLERANCE_MINUTES = 5
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

const atTime = (localDate, hhmm) =>
  TIME_RE.test(String(hhmm || ''))
    ? moment(`${localDate} ${hhmm}`, 'YYYY-MM-DD HH:mm', true)
    : null

/** "45 min" / "1 hr" / "1 hr 25 min". */
export function formatCoolingDuration(minutes) {
  const n = Math.round(Number(minutes))
  if (!Number.isFinite(n) || n < 0) return ''
  if (n < 60) return `${n} min`
  const hours = Math.floor(n / 60)
  const rest = n % 60
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`
}

/**
 * Start / finish for the draft.
 * @returns {{ startAt: string|null, endAt: string|null, minutes: number|null,
 *   finishIsNow: boolean, error: string|null }}
 */
export function resolveCoolingWindow({
  localDate,
  viewingToday,
  startTime,
  finishTime,
  now = moment(),
}) {
  const result = {
    startAt: null,
    endAt: null,
    minutes: null,
    finishIsNow: false,
    error: null,
  }
  const current = moment(now)
  let finish = atTime(localDate, finishTime)
  if (!finish && viewingToday) {
    finish = current.clone().seconds(0).milliseconds(0)
    result.finishIsNow = true
  }
  if (
    finish &&
    finish.diff(current, 'minutes', true) > FUTURE_TOLERANCE_MINUTES
  ) {
    return { ...result, error: 'Finish time can’t be in the future.' }
  }
  if (finish) result.endAt = finish.toISOString()

  let start = atTime(localDate, startTime)
  if (!start) return result
  if (!finish) {
    return { ...result, error: 'Enter when it finished cooling.' }
  }
  // Started late evening, finished after midnight.
  if (start.isAfter(finish)) start = start.subtract(1, 'day')
  const minutes = Math.round(finish.diff(start, 'minutes', true))
  if (minutes > LONGEST_WINDOW_MINUTES) {
    return {
      ...result,
      error:
        'Start and finish are more than 48 hours apart — check the start time.',
    }
  }
  return { ...result, startAt: start.toISOString(), minutes }
}

/** HH:mm `minutes` before the finish (or now). */
export function coolingStartTimeBefore({
  localDate,
  finishTime,
  minutes,
  now = moment(),
}) {
  const finish = atTime(localDate, finishTime) || moment(now)
  return finish.clone().subtract(minutes, 'minutes').format('HH:mm')
}

export function isCoolingTimeExceeded(minutes, maxMinutes) {
  const took = Number(minutes)
  const max = Number(maxMinutes)
  return (
    minutes != null &&
    maxMinutes != null &&
    Number.isFinite(took) &&
    Number.isFinite(max) &&
    took > max
  )
}

/** Same words as the saved exception headline. */
export function formatCoolingTimeHeadline(minutes, maxMinutes) {
  return `⚠ Cooling took ${Math.round(
    Number(minutes),
  )} min — over the ${maxMinutes} min limit`
}

/** "Took 1 hr 25 min" (+ max) — or a nudge when there's no start time. */
export function formatCoolingDurationLine(minutes, maxMinutes) {
  if (minutes == null) {
    return 'Add when it went in to record how long it took.'
  }
  const took = `Took ${formatCoolingDuration(minutes)}`
  if (maxMinutes == null) return took
  return isCoolingTimeExceeded(minutes, maxMinutes)
    ? `${took} — over the ${formatCoolingDuration(maxMinutes)} limit`
    : `${took} — within ${formatCoolingDuration(maxMinutes)}`
}

/**
 * Composer final-temp config from the day's `coolingTarget`. A template's own
 * range wins; null when Express sent no target (suggested ≤8°C applies).
 */
export function coolingTargetTempConfig(target, templateConfig) {
  if (templateConfig?.tempMin != null || templateConfig?.tempMax != null) {
    return templateConfig
  }
  if (!target || typeof target !== 'object') return null
  if (target.noTarget || target.tempMax == null) return { noRange: true }
  return { tempMax: Number(target.tempMax) }
}

/** Saved log extras: "from 70°C", "Took 1 hr 25 min". */
export function formatCoolingLogExtras(payload = {}) {
  const extras = []
  const startTemp = payload?.startTemp
  if (
    startTemp != null &&
    startTemp !== '' &&
    Number.isFinite(Number(startTemp))
  ) {
    extras.push(`from ${Number(startTemp)}°C`)
  }
  const minutes = payload?.coolingMinutes
  if (minutes != null && Number.isFinite(Number(minutes))) {
    extras.push(`took ${formatCoolingDuration(minutes)}`)
  }
  return extras
}

/* Venue cooling target card (Setup → Cooling). Mirrors Express PATCH rules. */

export const FOOD_SAFETY_COOLING_TEMP_LIMITS = Object.freeze({
  min: -50,
  max: 150,
})

export const FOOD_SAFETY_COOLING_MAX_MINUTES_LIMITS = Object.freeze({
  min: 5,
  max: 1440,
})

export const FOOD_SAFETY_COOLING_NO_TARGET_LABEL =
  'No target — final temps aren’t flagged'

const toCoolingNumber = value => {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * "8°C or below · no time limit" / "5°C or below within 1 hr 30 min".
 * `target` is GET /settings `coolingTarget` (or the suggested target).
 */
export function formatFoodSafetyCoolingTarget(target) {
  const source = target || FOOD_SAFETY_COOLING_SUGGESTED_TARGET
  const tempMax = toCoolingNumber(source.tempMax)
  const maxMinutes = toCoolingNumber(source.maxMinutes)
  const temp =
    source.noTarget || tempMax == null
      ? FOOD_SAFETY_COOLING_NO_TARGET_LABEL
      : `${tempMax}°C or below`
  const time =
    maxMinutes == null
      ? 'no time limit'
      : `within ${formatCoolingDuration(maxMinutes)}`
  return `${temp} · ${time}`
}

/** Card draft from GET /settings `coolingTarget`. */
export function coolingTargetDraftFromSettings(target, remindAfterMinutes) {
  const source = target || {
    ...FOOD_SAFETY_COOLING_SUGGESTED_TARGET,
    noTarget: false,
  }
  const tempMax = toCoolingNumber(source.tempMax)
  const noTarget = Boolean(source.noTarget) || tempMax == null
  const maxMinutes = toCoolingNumber(source.maxMinutes)
  return {
    noTarget,
    tempMax: noTarget ? '' : tempMax,
    maxMinutes: maxMinutes == null ? '' : maxMinutes,
    remindAfterMinutes: resolveCoolingRemindAfterMinutes({
      remindAfterMinutes:
        remindAfterMinutes ??
        source.remindAfterMinutes ??
        source.coolingRemindAfterMinutes,
    }),
  }
}

/** Error string or null. */
export function validateFoodSafetyCoolingTargetDraft(draft) {
  if (!draft?.noTarget) {
    const raw = draft?.tempMax
    if (raw == null || raw === '') {
      return 'Set a target temperature — or choose No target.'
    }
    const n = Number(raw)
    if (!Number.isFinite(n)) return 'Cooling target must be a number.'
    const { min, max } = FOOD_SAFETY_COOLING_TEMP_LIMITS
    if (n < min || n > max) {
      return `Cooling target must be between ${min} and ${max}°C.`
    }
  }
  const rawMinutes = draft?.maxMinutes
  if (rawMinutes != null && rawMinutes !== '') {
    const m = Number(rawMinutes)
    const { min, max } = FOOD_SAFETY_COOLING_MAX_MINUTES_LIMITS
    if (!Number.isInteger(m) || m < min || m > max) {
      return `Max cooling time must be whole minutes between ${min} and ${max}.`
    }
  }
  const rawRemind = draft?.remindAfterMinutes
  if (rawRemind != null && rawRemind !== '') {
    const r = Number(rawRemind)
    const { min, max } = FOOD_SAFETY_COOLING_REMIND_AFTER_LIMITS
    if (!Number.isInteger(r) || r < min || r > max) {
      return `Check-after time must be whole minutes between ${min} and ${max}.`
    }
  }
  return null
}

/** PATCH /settings body. A blank max time means no time limit. */
export function buildFoodSafetyCoolingTargetPatch(draft) {
  const remind = toCoolingNumber(draft?.remindAfterMinutes)
  return {
    coolingTarget: {
      tempMax: draft?.noTarget ? null : toCoolingNumber(draft?.tempMax),
      maxMinutes: toCoolingNumber(draft?.maxMinutes),
    },
    coolingRemindAfterMinutes:
      remind == null ? FOOD_SAFETY_COOLING_REMIND_AFTER_DEFAULT : remind,
  }
}

/** PATCH /settings body that goes back to the suggested ≤8°C, no time limit. */
export function buildFoodSafetyCoolingTargetResetPatch() {
  return {
    coolingTarget: null,
    coolingRemindAfterMinutes: null,
  }
}

export const FOOD_SAFETY_COOLING_REMIND_AFTER_DEFAULT = 90
export const FOOD_SAFETY_COOLING_REMIND_AFTER_LIMITS = Object.freeze({
  min: 5,
  max: 1440,
})

export function resolveCoolingRemindAfterMinutes(settingsOrTarget) {
  const n = Number(
    settingsOrTarget?.coolingRemindAfterMinutes ??
      settingsOrTarget?.remindAfterMinutes,
  )
  return Number.isInteger(n) && n > 0
    ? n
    : FOOD_SAFETY_COOLING_REMIND_AFTER_DEFAULT
}

/** Start instant for "Start cooling" (finish comes later). */
export function resolveCoolingStartAt({
  localDate,
  viewingToday,
  startTime,
  now = moment(),
}) {
  const current = moment(now)
  let start = atTime(localDate, startTime)
  if (!start && viewingToday) {
    start = current.clone().seconds(0).milliseconds(0)
  }
  if (!start) {
    return { startAt: null, error: 'Say when it went in to cool.' }
  }
  if (start.diff(current, 'minutes', true) > FUTURE_TOLERANCE_MINUTES) {
    return { startAt: null, error: 'Start time can’t be in the future.' }
  }
  return { startAt: start.toISOString(), error: null }
}

export function coolingElapsedMinutes(startAt, now = moment()) {
  if (!startAt) return null
  const start = moment(startAt)
  if (!start.isValid()) return null
  const minutes = Math.max(
    0,
    Math.round(moment(now).diff(start, 'minutes', true)),
  )
  return minutes
}

/** "45 min in · remind at 1 hr 30 min" / "Over the 90 min reminder". */
export function formatCoolingElapsedVsTarget({
  startAt,
  remindAfterMinutes,
  now = moment(),
} = {}) {
  const elapsed = coolingElapsedMinutes(startAt, now)
  if (elapsed == null) return ''
  const target = Number(remindAfterMinutes)
  const took = formatCoolingDuration(elapsed)
  if (!Number.isFinite(target) || target <= 0) return `${took} in`
  const limit = formatCoolingDuration(target)
  return elapsed > target
    ? `${took} in — over the ${limit} reminder`
    : `${took} in · remind at ${limit}`
}

export function formatCoolingOpenBatchesLine(names = [], count = 0) {
  const labels = (names || []).filter(Boolean)
  const n = Number(count) || labels.length
  if (n <= 0) return ''
  if (n === 1 && labels[0]) return `Still cooling: ${labels[0]}`
  if (n === 1) return '1 batch still cooling'
  if (labels.length) {
    const shown = labels.slice(0, 3)
    const extra = n - shown.length
    return extra > 0
      ? `Still cooling: ${shown.join(', ')} +${extra} more`
      : `Still cooling: ${shown.join(', ')}`
  }
  return `${n} batches still cooling`
}

export function listActiveCoolingBatches(day) {
  const fromApi = Array.isArray(day?.activeBatches) ? day.activeBatches : []
  if (fromApi.length) return fromApi
  const logs = [
    ...(day?.items || []).flatMap(item => item.logs || []),
    ...(day?.orphanLogs || []),
  ]
  return logs.filter(log => log?.status === 'in_progress')
}
